import { format, parse } from 'date-fns';

import {
  GetProgramInstituteCoursesResponseType,
  GetUserOrdersDetailsResponseType
} from '@nw/shared/data-access';
import { getFormattedDate } from '@nw/shared/utils';

import { UserProgramInstituteCourseRow } from '../enrollmentCollegeSelectionUtils';

/** Program blocks returned on `user_orders_details` (not the full API wrapper object). */
export type UserOrdersDetailsListType =
  GetUserOrdersDetailsResponseType['user_orders_details'];

/** When present under NIAT, paid amounts for institute enrollment are read from this order, not the college `product_code`. */
const NIAT_TERM_ONE_FEE_PRODUCT_CODE = 'NIAT_TERM_ONE_FEE';

export const getOrderDetailByProductCode = (
  userOrders: UserOrdersDetailsListType,
  productCode: string,
  program: string
) => {
  const programDetail = userOrders.find((order) => order.program === program);
  const order = programDetail?.registered.find(
    (order) => order.product_code === productCode
  );
  return order?.user_order_details;
};

/** Registered first, then expired — used when a completed enrollment fee may sit in `expired`. */
export const getOrderDetailByProductCodeIncludingExpired = (
  userOrders: UserOrdersDetailsListType,
  productCode: string,
  program: string
) => {
  const programDetail = userOrders.find((order) => order.program === program);
  const registeredOrder = programDetail?.registered.find(
    (order) => order.product_code === productCode
  );
  if (registeredOrder?.user_order_details) {
    return registeredOrder.user_order_details;
  }
  const expiredOrder = programDetail?.expired.find(
    (order) => order.product_code === productCode
  );
  return expiredOrder?.user_order_details;
};

export const getAmountPaidByProductCodeFromUserOrders = (
  userOrders: UserOrdersDetailsListType | null | undefined,
  productCode: string,
  program: string
): number | null => {
  if (!userOrders?.length || !productCode.trim()) {
    return null;
  }
  const hasNiatTermOneOrder =
    program === 'NIAT' &&
    Boolean(
      getOrderDetailByProductCodeIncludingExpired(
        userOrders,
        NIAT_TERM_ONE_FEE_PRODUCT_CODE,
        program
      )
    );
  const resolvedProductCode = hasNiatTermOneOrder
    ? NIAT_TERM_ONE_FEE_PRODUCT_CODE
    : productCode;
  const orderDetail = getOrderDetailByProductCodeIncludingExpired(
    userOrders,
    resolvedProductCode,
    program
  );
  const amountPaidInLeastSubunit = Number(
    orderDetail?.amount_paid_in_least_subunit
  );
  if (Number.isNaN(amountPaidInLeastSubunit)) {
    return null;
  }
  return amountPaidInLeastSubunit / 100;
};

export const getEnrollmentCollegeAndAccommodationDisplay = (
  values: Record<string, any> | null | undefined
): {
  preferredUniversityDisplay: string;
  accommodationModeDisplay: string;
} => {
  const preferredUniversityDisplay = String(
    values?.preferredUniversity ??
      values?.preferred_university ??
      values?.PREFERRED_UNIVERSITY ??
      ''
  ).trim();

  const rawAccommodationMode = String(
    values?.accommodationMode ??
      values?.accomodationMode ??
      values?.accommodation_mode ??
      values?.ACCOMMODATION_MODE ??
      ''
  ).trim();

  const accommodationModeDisplay =
    rawAccommodationMode === 'DAY_SCHOLAR'
      ? 'Day Scholar'
      : rawAccommodationMode === 'HOSTELER'
        ? 'Hosteler'
        : rawAccommodationMode;

  return {
    preferredUniversityDisplay,
    accommodationModeDisplay
  };
};

export const getEnrollmentDisplayFromInstituteSelection = (params: {
  selectedInstituteData:
    | GetProgramInstituteCoursesResponseType
    | null
    | undefined;
  latestUserProgramInstituteCourseRow:
    | UserProgramInstituteCourseRow
    | null
    | undefined;
}): {
  preferredUniversityDisplay: string;
  accommodationModeDisplay: string;
} => {
  const { selectedInstituteData, latestUserProgramInstituteCourseRow } = params;
  const latestRowInstituteId =
    latestUserProgramInstituteCourseRow?.institute_id;
  const selectedInstituteId = selectedInstituteData?.institute?.institute_id;
  const selectedInstituteName =
    selectedInstituteData?.institute?.institute_name;
  const hasLatestRowInstituteId = Boolean(latestRowInstituteId);
  const hasSelectedInstituteId = Boolean(selectedInstituteId);
  const isInstituteMatched =
    hasLatestRowInstituteId &&
    hasSelectedInstituteId &&
    latestRowInstituteId === selectedInstituteId;

  const preferredUniversityDisplay =
    !hasLatestRowInstituteId && !hasSelectedInstituteId
      ? '-'
      : selectedInstituteName || '-';

  const rawAccommodationMode =
    isInstituteMatched || (!hasLatestRowInstituteId && !hasSelectedInstituteId)
      ? latestUserProgramInstituteCourseRow?.accommodation_mode ?? ''
      : '';
  const accommodationModeDisplay =
    rawAccommodationMode === 'DAY_SCHOLAR'
      ? 'Day Scholar'
      : rawAccommodationMode === 'HOSTELER'
        ? 'Hosteler'
        : rawAccommodationMode || '-';

  return {
    preferredUniversityDisplay,
    accommodationModeDisplay
  };
};

export const getSelectedDateFromOrders = (
  userOrders: UserOrdersDetailsListType,
  productCode: string
): string | null => {
  const niatProgram = userOrders.find((order) => order.program === 'NIAT');
  if (!niatProgram) return null;

  const targetOrder = niatProgram.registered.find(
    (order) => order.product_code === productCode
  );
  if (!targetOrder) return null;

  const extraInfo =
    targetOrder.user_order_details.extra_info_based_on_product_config;
  if (!extraInfo) return null;

  try {
    const parsedExtraInfo = JSON.parse(extraInfo);
    if (parsedExtraInfo.segmentDateTime) {
      const formattedDate = getFormattedDate(
        parsedExtraInfo.segmentDateTime,
        'do MMMM, h aa'
      );
      return formattedDate || null;
    } else return getFormattedDateFromValues(parsedExtraInfo);
  } catch (error) {
    return null;
  }
};

export const getFormattedDateFromValues = (
  values: Record<string, any>
): string | null => {
  const {
    preferredDateToVisitCampus,
    preferredSlotToVisitCampus,
    preferredDateToScheduleInterview,
    preferredSlotToInterview,
    selectADateToBookASlot,
    timeSlots
  } = values;

  const preferredDate =
    preferredDateToScheduleInterview ??
    preferredDateToVisitCampus ??
    selectADateToBookASlot;

  const preferredSlot =
    preferredSlotToInterview ?? preferredSlotToVisitCampus ?? timeSlots;

  if (!preferredDate || !preferredSlot) return null;
  let dateFormat = 'yyyy-MM-dd';
  try {
    let parsedDate;
    if (selectADateToBookASlot) {
      parsedDate = parse(selectADateToBookASlot, dateFormat, new Date());
    } else {
      parsedDate = parse(preferredDate, dateFormat, new Date());
    }

    return format(parsedDate, 'do MMMM') + ', ' + preferredSlot;
  } catch (error) {
    return null;
  }
};
